import { useCallback } from "react";

export function useAudioPlayer() {
  const playAudio = useCallback((url, onComplete) => {
    const safeCallback = () => {
      if (typeof onComplete === "function") {
        onComplete();
      }
    };

    if (!url) {
      console.error("Invalid audio URL");
      safeCallback();
      return;
    }

    const audio = new Audio();
    let loadTimeout;

    const cleanup = () => {
      clearTimeout(loadTimeout);
      audio.removeEventListener("error", handleError);
      audio.removeEventListener("ended", handleEnded);
      audio.removeEventListener("canplaythrough", handleCanPlay);
    };

    const handleError = (e) => {
      console.error("Audio playback error:", e);
      cleanup();
      safeCallback();
    };

    const handleEnded = () => {
      cleanup();
      safeCallback();
    };

    const handleCanPlay = () => {
      clearTimeout(loadTimeout);
      const playPromise = audio.play();
      if (playPromise !== undefined) {
        playPromise.catch((error) => {
          console.error("Playback failed:", error);
          handleError(error);
        });
      }
    };

    audio.addEventListener("error", handleError);
    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("canplaythrough", handleCanPlay);

    loadTimeout = setTimeout(() => {
      handleError(new Error("Audio loading timeout"));
    }, 5000);

    try {
      audio.src = url;
      audio.load();
    } catch (error) {
      handleError(error);
    }
  }, []);

  const playAudioSequence = useCallback(
    (urls, onComplete) => {
      const safeCallback = () => {
        if (typeof onComplete === "function") {
          onComplete();
        }
      };

      if (!Array.isArray(urls) || urls.length === 0) {
        console.error("Invalid audio URL array");
        safeCallback();
        return;
      }

      const validUrls = urls.filter(
        (url) => typeof url === "string" && url.length > 0
      );

      const playNext = (index) => {
        if (index >= validUrls.length) {
          safeCallback();
          return;
        }
        playAudio(validUrls[index], () => playNext(index + 1));
      };

      playNext(0);
    },
    [playAudio]
  );

  return { playAudioSequence };
}
